import React, { useContext, useEffect, useState } from 'react';
import AuthContext from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Base from '../components/Base'; // Assuming Base component exists
import axios from 'axios';
import { Button, Input, Textarea, Select, Alert, Card } from '../components/ui';
import COLORS from '../utils/colors';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
const MAX_FILE_SIZE_MB = 3;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;


// Reusable Image Upload Field Component
// This component is used by both UploadProduct and EditProduct.
const ImageUploadField = ({
  id,
  label,
  value, // {file: File, preview: string, error: string}
  onChange, // (value: {file: File, preview: string}, error: string) => void
  onDelete, // () => void
  error,
  showRequiredStar = true,
  altText = "Preview",
  className = "h-48 w-36 sm:h-52", // Default size for main images
}) => {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        // Pass original preview if file is too large, so existing image isn't lost
        onChange({ file: null, preview: value?.preview || null }, `File size must be ${MAX_FILE_SIZE_MB}MB or less.`);
        e.target.value = ''; // Clear file input value to allow re-selection of same file after error
        return;
      }
      const previewUrl = URL.createObjectURL(file); // Create a blob URL for immediate preview
      onChange({ file, preview: previewUrl }, null); // Pass file and its preview to parent
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation(); // Prevent triggering parent div's onClick
    onDelete(); // Notify parent to delete
    const inputElement = document.getElementById(id);
    if (inputElement) inputElement.value = ''; // Clear file input value
  };

  useEffect(() => {
    let currentPreview = value?.preview;
    // Cleanup function: revoke blob URL when component unmounts or preview changes
    return () => {
      if (currentPreview && currentPreview.startsWith('blob:')) {
        URL.revokeObjectURL(currentPreview);
      }
    };
  }, [value?.preview]); // Dependency array: run effect when value.preview changes


  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {showRequiredStar && <span className="text-red-500">*</span>}
        </label>
      )}
      <div
        className={`${className} border-2 border-dashed border-gray-300 flex items-center justify-center relative cursor-pointer bg-gray-50 rounded-md`}
        style={{ borderColor: value?.preview ? COLORS.AUTUMN : '' }}
        onMouseEnter={(e) => { if (!value?.preview) e.currentTarget.style.borderColor = COLORS.AUTUMN; }}
        onMouseLeave={(e) => { if (!value?.preview) e.currentTarget.style.borderColor = ''; }}
        onClick={() => document.getElementById(id)?.click()} // Simulate click on hidden file input
      >
        <input
          type="file"
          id={id}
          accept="image/*" // Accept all image types
          className="hidden" // Hide the default file input
          onChange={handleFileChange}
        />
        {value?.preview ? ( // If there's a preview URL, show the image
          <>
            <img src={value.preview} alt={altText} className="w-full h-full object-cover rounded-md" />
            <button
              type="button"
              onClick={handleDeleteClick}
              className="absolute top-1 right-1 bg-white text-red-500 p-1 rounded-full shadow hover:bg-red-100 text-xl leading-none"
              aria-label="Delete image"
            >
              &times;
            </button>
          </>
        ) : ( // Otherwise, show "Upload Image" text
          <span className="text-gray-500 text-center px-2 text-sm">Upload Image</span>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};


const UploadProduct = () => {
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);

  // Memoized Axios instance for authenticated requests, ensuring it recreates only if token changes
  const authAxios = React.useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  }), [authToken?.access]);

  // Initial form data for single product upload
  const initialFormData = {
    name: '',
    description: '',
    gender: '',
    type: '',
    category: '',
    fabric: '',
    color: '',
    price: '',
    discountedPrice: '',
    discountPercent: 0,
    days: '',
    stockQuantity: '',
    disclaimer: '',
    compositionAndCare: '',
    customizationPossible: 'no',
    mainImages: Array(6).fill(null).map(() => ({ file: null, preview: null, error: null, originalUrl: null })),
  };

  // State variables for the form
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({}); // Stores validation errors for form fields
  const [typeOptions, setTypeOptions] = useState([]); // Dynamic options for product type
  const [categoryOptions, setCategoryOptions] = useState([]); // Dynamic options for product category

  // State for Bulk Upload Modal and progress
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false); // Controls visibility of bulk upload modal
  const [selectedBulkFile, setSelectedBulkFile] = useState(null); // Stores the selected CSV file
  const [uploadProgress, setUploadProgress] = useState(0); // Tracks upload progress percentage

  // Effect to update Type options based on Gender selection
  useEffect(() => {
    if (formData.gender) {
      let options = [];
      if (formData.gender === 'Men') options = ['Indianwear', 'Westernwear'];
      else if (formData.gender === 'Women') options = ['Indianwear', 'Westernwear'];
      else if (formData.gender === 'Unisex') options = ['Unisex', 'Unisex'];
      else if (formData.gender === 'Kids') options = ['Boys', 'Girls'];
      setTypeOptions(options);
      setFormData(prev => ({ ...prev, type: '', category: '' })); // Reset type and category when gender changes
      setCategoryOptions([]);
    } else {
      setTypeOptions([]);
      setCategoryOptions([]);
    }
  }, [formData.gender]);

  // Effect to update Category options based on Gender and Type selection
  useEffect(() => {
    if (formData.gender && formData.type) {
      let categories = [];
      const MenIndian = ['Sherwanis', 'Bandhgalas', 'Kurtas', 'Kurta Sets', 'Nehru Jackets', 'Bandi Jackets', 'Dhoti Kurta', 'Pathani Suits', 'Jodhpuri Suits'];
      const MenWestern = ['Blazers', 'Casual Shirts', 'Formal Shirts', 'Over Sized Shirts', 'Suits and Tuxedos', 'Coats and Jackets', 'Trousers and Pants', 'Sleepwear'];
      const WomenIndian = ['Kurtis', 'Kurti sets', 'Blouses', 'Tunics', 'Shararas', 'Ghagras', 'Sharara and Gharara Sets', 'Lehengas', 'Sarees', 'Sarees sets', 'Suits', 'Salwar Suits', 'Anarkali Suits', 'Straight Cut Suits', 'Salwar Kameez', 'Dupattas', 'Indo-Western', 'Indo-Western Gowns', 'Capes and Overlays', 'Kaftans', 'Gowns', 'Trousers and Bottoms', 'Palazzos', 'Jackets'];
      const WomenWestern = ['Crop-Tops', 'Casual Tops', 'Dresses', 'Gowns', 'Tops and Blouses', 'Co-Ord Sets', 'Saree Gowns', 'Dhoti Pants', 'Jackets', 'Blazers', 'Jumpsuits and Playsuits', 'Trousers', 'Pants and Bottoms', 'Skirts', 'Jumpsuits', 'Sleepwear'];
      const KidsBoys = ['Top Wear', 'Bottom Wear', 'Co-Ord Sets'];
      const KidsGirls = ['Top Wear', 'Bottom Wear', 'Short Frocks', 'Long Frocks', 'Co-Ord Sets'];
      const Unisex = ['Top Wear', 'Bottom Wear', 'Co-Ord Sets'];
      
      if (formData.gender === 'Men') categories = formData.type === 'Indianwear' ? MenIndian : MenWestern;
      else if (formData.gender === 'Women') categories = formData.type === 'Indianwear' ? WomenIndian : WomenWestern; 
      else if (formData.gender === 'Kids') categories = formData.type === 'Boys' ? KidsBoys : KidsGirls;
      else if (formData.gender === 'Unisex') categories = formData.type === 'Boys' ? Unisex : Unisex;

      setCategoryOptions(categories);
      setFormData(prev => ({ ...prev, category: '' })); // Reset category when type changes
    } else {
      setCategoryOptions([]);
    }
  }, [formData.gender, formData.type]);

  // Handle generic input changes for form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for the field if user starts typing (optimistic error clearing)
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle main product image changes (when a new file is selected or preview changes)
  const handleMainImageChange = (index, imageValue, errorMsg) => {
    setFormData(prev => {
      const newImages = [...prev.mainImages];
      // Revoke old blob URL if it exists and is different from the new preview (prevent memory leaks)
      if (newImages[index]?.preview && newImages[index].preview.startsWith('blob:') && newImages[index].preview !== imageValue.preview) {
        URL.revokeObjectURL(newImages[index].preview);
      }
      newImages[index] = { ...newImages[index], ...imageValue, error: errorMsg }; // Update with new file/preview
      return { ...prev, mainImages: newImages };
    });
    // Clear mainImages error if applicable
    if (errors.mainImages) setErrors(prev => ({ ...prev, mainImages: null }));
  };

  // Handle main product image deletion
  const handleMainImageDelete = (index) => {
    setFormData(prev => {
      const newImages = [...prev.mainImages];
      // Revoke old blob URL if it exists
      if (newImages[index]?.preview && newImages[index].preview.startsWith('blob:')) {
        URL.revokeObjectURL(newImages[index].preview);
      }
      // Set file and preview to null to signal deletion or empty state
      newImages[index] = { ...newImages[index], file: null, preview: null, error: null }; 
      return { ...prev, mainImages: newImages };
    });
  };

  // Effect to calculate discount percentage based on price and discountedPrice
  useEffect(() => {
    const price = parseFloat(formData.price);
    const discountedPrice = parseFloat(formData.discountedPrice);
    if (!isNaN(price) && price > 0 && !isNaN(discountedPrice) && discountedPrice > 0 && discountedPrice < price) {
      const discount = ((price - discountedPrice) / price) * 100;
      setFormData(prev => ({ ...prev, discountPercent: discount.toFixed(2) }));
    } else if (formData.discountedPrice === '' || isNaN(discountedPrice) || discountedPrice <= 0 || discountedPrice >= price) {
      setFormData(prev => ({ ...prev, discountPercent: 0 }));
    }
  }, [formData.price, formData.discountedPrice]);

  // Client-side form validation logic
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Product name is required.';
    if (!formData.description.trim()) newErrors.description = 'Description is required.';
    if (!formData.gender) newErrors.gender = 'Gender is required.';
    if (!formData.type && formData.gender) newErrors.type = 'Type is required.'; // Type depends on gender
    if (!formData.category && formData.type) newErrors.category = 'Category is required.'; // Category depends on type
    if (!formData.fabric.trim()) newErrors.fabric = 'Fabric is required.';
    if (!formData.color.trim()) newErrors.color = 'Color is required.';
    if (!formData.price.trim()) newErrors.price = 'Price is required.';
    else if (!/^\d+(\.\d{1,2})?$/.test(formData.price) || parseFloat(formData.price) <= 0) newErrors.price = 'Enter a valid positive price.';
    if (formData.discountedPrice.trim()) {
      if (!/^\d+(\.\d{1,2})?$/.test(formData.discountedPrice) || parseFloat(formData.discountedPrice) <= 0) {
        newErrors.discountedPrice = 'Enter a valid positive discounted price.';
      } else if (parseFloat(formData.discountedPrice) >= parseFloat(formData.price)) {
        newErrors.discountedPrice = 'Discounted price must be less than the original price.';
      }
    }
    if (!formData.days) newErrors.days = 'Days to complete is required.';
    if (!formData.disclaimer.trim()) newErrors.disclaimer = 'Disclaimer is required.';
    if (!formData.compositionAndCare.trim()) newErrors.compositionAndCare = 'Composition and Care is required.';

    // Validate main images: at least one is required
    const hasAtLeastOneMainImage = formData.mainImages.some(img => img && (img.file || img.preview)); // Check for file or existing preview
    if (!hasAtLeastOneMainImage) {
      newErrors.mainImages = 'At least one product image is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  };

  // Handle single product form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      Swal.fire('Validation Error', 'Please correct the errors in the form.', 'error');
      // Scroll to the first error element for better UX
      const firstErrorElement = document.querySelector('.border-red-500, .text-red-500');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top if no specific error element found
      }
      return;
    }

    Swal.fire({ title: 'Uploading Product...', text: 'Please wait.', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    const payload = new FormData(); // Use FormData for multipart/form-data with files
    // Append all basic product fields
    payload.append('name', formData.name);
    payload.append('description', formData.description);
    payload.append('gender', formData.gender);
    payload.append('type', formData.type);
    payload.append('category', formData.category);
    payload.append('fabric', formData.fabric);
    payload.append('color', formData.color);
    payload.append('price', formData.price);
    if (formData.discountedPrice.trim()) payload.append('Discountedprice', formData.discountedPrice);
    if (parseFloat(formData.discountPercent) > 0) payload.append('Discountpercent', formData.discountPercent);
    payload.append('days', formData.days);
    if (formData.stockQuantity !== '') payload.append('stock_quantity', formData.stockQuantity);
    payload.append('disclaimer', formData.disclaimer);
    payload.append('compositionAndCare', formData.compositionAndCare);

    payload.append('customizationPossible', formData.customizationPossible === 'yes' ? '1' : '0');

    // Append main product images (only if a file is present)
    formData.mainImages.forEach((imgData, index) => {
      if (imgData && imgData.file) { // If a new file is selected
        payload.append(`img${index + 1}`, imgData.file, imgData.file.name);
      }
    });

    try {
      // Send POST request to upload new product
      const response = await authAxios.post('/upload_product/', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
      Swal.close(); // Close loading spinner

      if (response.status === 201 && response.data.success) { // HTTP 201 Created for success
        Swal.fire({
          icon: 'success', title: 'Design Added to Your Store!', text: response.data.message || 'Your design is now live on your branded store and visible to your customers.',
          confirmButtonText: 'View My Store', cancelButtonText: 'Add Another Design', showCancelButton: true,
        }).then((result) => {
          if (result.isConfirmed) navigate('/homepage/'); // Navigate to homepage
          else if (result.dismiss === Swal.DismissReason.cancel) {
            // Revoke all blob URLs to prevent memory leaks before resetting form
            formData.mainImages.forEach(imgData => {
              if (imgData?.preview && imgData.preview.startsWith('blob:')) URL.revokeObjectURL(imgData.preview);
            });
            setFormData(initialFormData); // Reset form to initial state
            setErrors({}); // Clear errors
            window.scrollTo(0, 0); // Scroll to top
          }
        });
      } else {
        // Handle non-201 but successful response (e.g., backend returns 200 with error message)
        Swal.fire('Upload Failed', response.data?.error || response.data?.details || 'An unexpected error occurred.', 'error');
      }
    } catch (error) {
      Swal.close(); // Close loading spinner
      devLog.error('Error uploading product:', error.response || error.message || error);
      let errorMessage = 'An error occurred while uploading the product.';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.error) errorMessage = errorData.error + (errorData.details ? ` (Details: ${JSON.stringify(errorData.details)})` : '');
        else if (typeof errorData === 'string') errorMessage = errorData;
        else if (errorData.detail) errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        else if (typeof errorData === 'object' && errorData !== null) {
            // Format generic object errors nicely
            errorMessage = Object.entries(errorData).map(([key, value]) => {
                let valStr = Array.isArray(value) ? value.join(', ') : String(value);
                if (valStr.length > 100) valStr = valStr.substring(0, 97) + "..."; // Truncate long messages
                return `${key}: ${valStr}`;
            }).join('; ');
            if (!errorMessage) errorMessage = JSON.stringify(errorData); // Fallback to stringify if still empty
        }
        if (errorMessage.length > 300) errorMessage = "Multiple validation errors. Please check form fields or console for details."; // Shorten very long messages
      } else if (error.message) errorMessage = error.message; // Generic network error
      Swal.fire('Upload Failed', errorMessage, 'error');
      if (error.response?.status === 401) navigate('/login'); // Redirect to login on 401 Unauthorized
    }
  };

  // Utility data for form rendering (e.g., labels, day options, CSS classes)
  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1); // Generates 1 to 28 days
  // Helper to dynamically apply input styling and error classes
  const getInputClass = (fieldName) => `w-full p-3 border rounded-md shadow-sm focus:ring-2 focus:border-gray-400 bg-gray-50 text-base ${errors[fieldName] ? 'border-red-500' : 'border-gray-300'}`;

  // Global cleanup effect: revoke all blob URLs when component unmounts
  useEffect(() => {
    const currentFormDataForCleanup = formData; // Capture current state for cleanup
    return () => {
      currentFormDataForCleanup.mainImages.forEach(imgData => {
        if (imgData?.preview && imgData.preview.startsWith('blob:')) URL.revokeObjectURL(imgData.preview);
      });
    };
  }, []);


  // --- NEW: Bulk Upload Specific Functions ---

  // Handle selection of the CSV file for bulk upload
  const handleBulkFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.csv')) { // Validate file extension
      setSelectedBulkFile(file);
      setUploadProgress(0); // Reset progress when a new file is selected
    } else {
      setSelectedBulkFile(null);
      Swal.fire('Invalid File', 'Please select a CSV file.', 'error');
    }
  };

  // Handle the bulk upload process (sending CSV to backend)
  const handleBulkUpload = async () => {
    if (!selectedBulkFile) {
      Swal.fire('No File Selected', 'Please choose a CSV file to upload.', 'warning');
      return;
    }

    // Show SweetAlert with progress bar
    Swal.fire({
      title: 'Uploading Products...',
      html: `
        <div class="swal2-content">
          <p>Please wait, uploading CSV file...</p>
          <div class="progress-bar-container" style="width: 100%; background-color: #f3f3f3; border-radius: 5px; overflow: hidden; margin-top: 10px;">
            <div class="progress-bar" style="width: 0%; height: 20px; background-color: #f97316; text-align: center; color: white; line-height: 20px; border-radius: 5px;">0%</div>
          </div>
          <p class="status-text" style="margin-top: 10px; font-size: 0.9em; color: #555;">Initializing...</p>
        </div>
      `,
      allowOutsideClick: false, // Prevent closing by clicking outside
      didOpen: () => {
        Swal.showLoading(); // Show loading spinner within the dialog
        // Get references to elements in the custom HTML for updating progress
        const statusText = Swal.getHtmlContainer().querySelector('.status-text');
        if (statusText) statusText.textContent = `Uploading ${selectedBulkFile.name}...`;
      },
      willClose: () => {
        setUploadProgress(0); // Ensure progress is reset when dialog closes
      }
    });

    const formDataForBulk = new FormData(); // Create FormData for the CSV file
    formDataForBulk.append('file', selectedBulkFile);

    try {
      // Send POST request for bulk upload with onUploadProgress callback
      const response = await authAxios.post('/bulk_upload_products/', formDataForBulk, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          const progressBar = Swal.getHtmlContainer().querySelector('.progress-bar');
          const statusText = Swal.getHtmlContainer().querySelector('.status-text');
          if (progressBar) {
            progressBar.style.width = `${percentCompleted}%`;
            progressBar.textContent = `${percentCompleted}%`;
          }
          if (statusText) {
            if (percentCompleted === 100) {
              statusText.textContent = "Upload complete. Processing products on server...";
            } else {
              statusText.textContent = `Uploading ${selectedBulkFile.name}... ${percentCompleted}%`;
            }
          }
        },
      });

      Swal.close(); // Close the progress dialog after response is received

      if (response.status === 201 && response.data.success) { // HTTP 201 Created (full success)
        Swal.fire({
          icon: 'success',
          title: 'Bulk Upload Complete!',
          html: `Successfully uploaded **${response.data.uploaded_count}** products.`,
          confirmButtonText: 'OK',
        });
      } else if (response.status === 207 && !response.data.success) { // HTTP 207 Multi-Status (partial success/failures)
        let errorMessageHtml = `<p>Processed **${response.data.uploaded_count}** products with **${response.data.errors.length}** errors.</p>`;
        if (response.data.errors && response.data.errors.length > 0) {
          const errorListHtml = response.data.errors.map(err =>
            // Display row number and error message clearly
            `<li style="text-align: left; margin-bottom: 5px;">
               <strong style="color: #d33;">Row ${err.row_number}:</strong> ${err.error}
               ${err.details ? `<br/><small style="color: #666;">(Details: ${err.details})</small>` : ''}
            </li>`
          ).join('');
          errorMessageHtml += `<h4 style="margin-top: 15px; color: #d33;">Details:</h4><ul style="list-style-type: none; padding: 0; max-height: 200px; overflow-y: auto; border: 1px solid #eee; padding: 10px; border-radius: 5px;">${errorListHtml}</ul>`;
          
          // Add option to download errors if there are many
          if (response.data.errors.length > 5) { // Arbitrary threshold
              errorMessageHtml += `<button id="downloadErrorsBtn" style="margin-top: 15px; background-color: #007bff; color: white; padding: 8px 15px; border-radius: 5px; cursor: pointer; border: none;">Download Error Log</button>`;
          }
        }

        Swal.fire({
          icon: 'warning', // Use warning icon for partial success/errors
          title: 'Bulk Upload Completed With Issues',
          html: errorMessageHtml,
          showCancelButton: false,
          confirmButtonText: 'OK',
          didRender: () => { // Add event listener for download button after SweetAlert is rendered
              if (response.data.errors.length > 5) {
                  document.getElementById('downloadErrorsBtn').addEventListener('click', () => {
                      const errorCsvContent = "Row,Error\n" + response.data.errors.map(err => 
                          `"${err.row_number}","${err.error.replace(/"/g, '""')}"` // Escape quotes for CSV
                      ).join('\n');
                      const blob = new Blob([errorCsvContent], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      link.href = URL.createObjectURL(blob);
                      link.download = 'bulk_upload_errors.csv';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                  });
              }
          }
        });
      } else {
         // Handle other non-200 responses from backend
         Swal.fire('Bulk Upload Failed', response.data?.error || response.data?.details || 'An unexpected error occurred.', 'error');
      }
      setShowBulkUploadModal(false); // Close the modal
      setSelectedBulkFile(null); // Clear selected file
      setUploadProgress(0); // Reset progress

    } catch (error) {
      Swal.close(); // Close loading dialog on error
      devLog.error('Error during bulk upload:', error.response || error.message || error);
      let errorMessage = 'An unexpected error occurred during bulk upload.';
      if (error.response?.data?.error) { // Specific error from backend
          errorMessage = error.response.data.error;
      } else if (error.response?.data?.details) { // More details from backend
          errorMessage = error.response.data.details;
      } else if (error.message) { // Network error or client-side issue
          errorMessage = error.message;
      } else {
        errorMessage = "Network error or server unavailable. Please try again.";
      }
      Swal.fire('Bulk Upload Error', errorMessage, 'error');
      setShowBulkUploadModal(false); // Close modal on critical error
      setSelectedBulkFile(null); // Clear selected file
      setUploadProgress(0); // Reset progress
    }
  };

  // Handles downloading the CSV template file
  const handleDownloadTemplate = () => {
    // Define all expected CSV headers
    const headers = [
      "Name", "Description", "Gender", "Type", "Category", "Fabric", "Color", "Price",
      "DiscountedPrice", "DaysToComplete", "Disclaimer", "CompositionAndCare", "CustomizationPossible",
      "FabricOption1Name", "FabricOption1Price", "FabricOption1ImageURL",
      "FabricOption2Name", "FabricOption2Price", "FabricOption2ImageURL",
      "FabricOption3Name", "FabricOption3Price", "FabricOption3ImageURL",
      "BorderOption1Name", "BorderOption1Price", "BorderOption1ImageURL",
      "BorderOption2Name", "BorderOption2Price", "BorderOption2ImageURL",
      "BorderOption3Name", "BorderOption3Price", "BorderOption3ImageURL",
      "BorderDesignOption1Name", "BorderDesignOption1Price", "BorderDesignOption1ImageURL",
      "BorderDesignOption2Name", "BorderDesignOption2Price", "BorderDesignOption2ImageURL",
      "BorderDesignOption3Name", "BorderDesignOption3Price", "BorderDesignOption3ImageURL"
    ];
    const csvContent = headers.join(',') + '\n'; // Join headers with commas and add newline
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }); // Create a Blob
    const link = document.createElement('a'); // Create a temporary anchor element
    if (link.download !== undefined) { // Feature detection for download attribute
      const url = URL.createObjectURL(blob); // Create object URL for the blob
      link.setAttribute('href', url);
      link.setAttribute('download', 'product_bulk_upload_template.csv'); // Set download filename
      link.style.visibility = 'hidden'; // Hide the link
      document.body.appendChild(link); // Append to body
      link.click(); // Programmatically click to trigger download
      document.body.removeChild(link); // Remove from body
      URL.revokeObjectURL(url); // Clean up the object URL
    }
  };

  return (
    <div className="min-h-screen font-lora text-gray-800">
      <Base Content={
        <div className="container mx-auto px-2 sm:px-4 py-8 max-w-7xl">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl text-center font-bold text-[#d45614] mb-2">
              Add New Design to Your Store
            </h1>
            <p className="text-center text-gray-600 mb-4">
              Showcase your design to YOUR customers on YOUR branded store
            </p>
            <div className="w-full h-px bg-gray-300"></div>
          </div>

          {/* NEW: Bulk Upload Button - Changed color to orange-600 */}
          <div className="text-center mb-6">
            <button
              type="button"
              onClick={() => setShowBulkUploadModal(true)} // Opens the bulk upload modal
              className="  text-white font-semibold py-2 px-5 rounded-lg shadow-md transition duration-150 ease-in-out" style={{ backgroundColor: COLORS.AUTUMN }}
            >
              Bulk Add Designs to Store
            </button>
          </div>

          <form onSubmit={handleSubmit} className="rounded-lg p-4 sm:p-8 bg-white shadow-xl">
            {/* Product Images Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <label className="block text-lg font-medium text-gray-700 mb-1">
                Product Images (Up to 6, at least 1 required) <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">
                📸 Upload high-quality photos of your design. The first image will be the main product photo shown to customers. Include different angles for better presentation. Max size: 3MB per image.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4 justify-center sm:justify-start">
                {formData.mainImages.map((imgData, index) => (
                  <ImageUploadField
                    key={`mainImg-${index}`}
                    id={`mainImg${index + 1}`}
                    value={imgData}
                    onChange={(value, error) => handleMainImageChange(index, value, error)}
                    onDelete={() => handleMainImageDelete(index)}
                    error={imgData?.error}
                    altText={`Product Image ${index + 1}`}
                    showRequiredStar={false}
                  />
                ))}
              </div>
              {errors.mainImages && <p className="text-red-500 text-sm mt-2">{errors.mainImages}</p>}
            </div>

            {/* Product Details Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Product Details</h3>
              <div className="space-y-6">
                {[
                  { label: 'Name of the product', name: 'name', type: 'text', helpText: '✏️ Give your design a clear, descriptive name that customers will easily understand (e.g., "Floral Embroidered Kurta Set", "Modern Slim Fit Blazer")' },
                  { label: 'Description', name: 'description', type: 'textarea', rows: 3, helpText: '📝 Describe your design in detail. Include key features, unique elements, and what makes it special. This helps customers understand what they\'re buying!' },
                ].map(field => (
                  <div key={field.name}>
                    <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label} <span className="text-red-500">*</span>
                    </label>
                    {field.helpText && <p className="text-xs text-gray-600 mb-2">{field.helpText}</p>}
                    {field.type === 'textarea' ? (
                      <textarea id={field.name} name={field.name} value={formData[field.name]} onChange={handleInputChange} rows={field.rows} className={getInputClass(field.name)} />
                    ) : (
                      <input type={field.type} id={field.name} name={field.name} value={formData[field.name]} onChange={handleInputChange} className={getInputClass(field.name)} />
                    )}
                    {errors[field.name] && <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>}
                  </div>
                ))}
                <div className="mb-2">
                  <p className="text-xs text-gray-600">👗 Select the appropriate category for your design. Choose Gender first, then Type (Indian/Western wear), then the specific Category.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">Gender <span className="text-red-500">*</span></label>
                    <select id="gender" name="gender" value={formData.gender} onChange={handleInputChange} className={getInputClass('gender')}>
                      <option value="">Select Gender</option><option value="Men">Men</option><option value="Women">Women</option><option value="Kids">Kids</option><option value="Unisex">Unisex</option>
                    </select>
                    {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
                  </div>
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">Type <span className="text-red-500">*</span></label>
                    <select id="type" name="type" value={formData.type} onChange={handleInputChange} disabled={!formData.gender} className={getInputClass('type')}>
                      <option value="">Select Type</option>{typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    {errors.type && <p className="text-red-500 text-xs mt-1">{errors.type}</p>}
                  </div>
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                    <select id="category" name="category" value={formData.category} onChange={handleInputChange} disabled={!formData.type} className={getInputClass('category')}>
                      <option value="">Select Category</option>{categoryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fabric" className="block text-sm font-medium text-gray-700 mb-1">Main Product Fabric <span className="text-red-500">*</span></label>
                    <p className="text-xs text-gray-600 mb-2">🧵 Specify the fabric material (e.g., Cotton, Silk, Linen, Georgette, Polyester)</p>
                    <input type="text" id="fabric" name="fabric" value={formData.fabric} onChange={handleInputChange} className={getInputClass('fabric')} placeholder="e.g., Pure Cotton" />
                    {errors.fabric && <p className="text-red-500 text-xs mt-1">{errors.fabric}</p>}
                  </div>
                  <div>
                    <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">Color <span className="text-red-500">*</span></label>
                    <p className="text-xs text-gray-600 mb-2">🎨 Enter the primary color of your design (e.g., Navy Blue, Maroon, Pastel Pink)</p>
                    <input type="text" id="color" name="color" value={formData.color} onChange={handleInputChange} className={getInputClass('color')} placeholder="e.g., Royal Blue" />
                    {errors.color && <p className="text-red-500 text-xs mt-1">{errors.color}</p>}
                  </div>
                </div>
              </div>
            </div>


            {/* Pricing Section */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Pricing</h3>
              <p className="text-sm text-gray-600 mb-4">💰 Set your pricing strategy. Enter the regular price, and optionally add a discounted price to show special offers to customers.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">Price (₹) <span className="text-red-500">*</span></label>
                  <input type="text" id="price" name="price" value={formData.price} onChange={handleInputChange} placeholder="e.g. 1000" className={getInputClass('price')} />
                  {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
                </div>
                <div>
                  <label htmlFor="discountedPrice" className="block text-sm font-medium text-gray-700 mb-1">Discounted Price (₹) <span className="text-gray-500 text-xs">(Optional)</span></label>
                  <p className="text-xs text-gray-600 mb-2">💸 If you're offering a discount, enter the sale price here. The discount % will be calculated automatically.</p>
                  <input type="text" id="discountedPrice" name="discountedPrice" value={formData.discountedPrice} onChange={handleInputChange} placeholder="e.g. 800" className={getInputClass('discountedPrice')} />
                  {errors.discountedPrice && <p className="text-red-500 text-xs mt-1">{errors.discountedPrice}</p>}
                </div>
              </div>
              {parseFloat(formData.discountPercent) > 0 && (
                <p className="mt-3 text-md font-semibold text-green-600">Calculated Discount: {formData.discountPercent}%</p>
              )}
            </div>


            {/* Customization Toggle */}
            <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Customization</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Is this product customizable?</label>
                <div className="flex items-center gap-x-6">
                  <label className="flex items-center text-sm"><input type="radio" name="customizationPossible" value="yes" checked={formData.customizationPossible === 'yes'} onChange={handleInputChange} className="h-4 w-4 border-gray-300 focus:ring-2" style={{ accentColor: COLORS.AUTUMN }} /><span className="ml-2">Yes</span></label>
                  <label className="flex items-center text-sm"><input type="radio" name="customizationPossible" value="no" checked={formData.customizationPossible === 'no'} onChange={handleInputChange} className="h-4 w-4 border-gray-300 focus:ring-2" style={{ accentColor: COLORS.AUTUMN }} /><span className="ml-2">No</span></label>
                </div>
              </div>
            </div>

            {/* Additional Information Section */}
             <div className="mb-8 p-4 border border-gray-200 rounded-md bg-gray-50/50">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Additional Information</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="days" className="block text-sm font-medium text-gray-700 mb-1">Days to Complete <span className="text-red-500">*</span></label>
                  <p className="text-xs text-gray-600 mb-2">⏱️ How many days will you need to complete and deliver this design to the customer? Be realistic to manage customer expectations.</p>
                  <select id="days" name="days" value={formData.days} onChange={handleInputChange} className={getInputClass('days')}>
                    <option value="">Select days</option>
                    {dayOptions.map(day => <option key={day} value={day}>{day} day{day > 1 ? 's' : ''}</option>)}
                  </select>
                  {errors.days && <p className="text-red-500 text-xs mt-1">{errors.days}</p>}
                </div>
                <div>
                  <label htmlFor="stockQuantity" className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity</label>
                  <p className="text-xs text-gray-600 mb-2">📦 How many units of this product do you have in stock? Leave empty or 0 if you make to order.</p>
                  <input type="number" id="stockQuantity" name="stockQuantity" value={formData.stockQuantity} onChange={handleInputChange} min="0" className={getInputClass('stockQuantity')} placeholder="e.g., 10" />
                </div>
                {[
                  { label: 'Disclaimer', name: 'disclaimer', rows: 3, helpText: '⚠️ Important information for customers, such as: "Slight color variations may occur due to screen settings" or "Embroidery details may vary slightly from the image"' },
                  { label: 'Composition and Care', name: 'compositionAndCare', rows: 3, helpText: '🧼 Care instructions for the garment. Example: "100% Cotton. Machine wash cold. Do not bleach. Iron on low heat." This helps customers maintain the product quality.' },
                ].map(field => (
                  <div key={field.name}>
                    <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label} <span className="text-red-500">*</span>
                    </label>
                    {field.helpText && <p className="text-xs text-gray-600 mb-2">{field.helpText}</p>}
                    <textarea id={field.name} name={field.name} value={formData[field.name]} onChange={handleInputChange} rows={field.rows} className={getInputClass(field.name)} placeholder={field.name === 'disclaimer' ? 'e.g., Colors may vary slightly...' : 'e.g., 100% Cotton. Machine wash cold...'} />
                    {errors[field.name] && <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>}
                  </div>
                ))}
              </div>
            </div>

            <button type="submit" className="w-full mt-6   text-white font-semibold py-3 px-6 rounded-lg transition duration-150 ease-in-out" style={{ backgroundColor: COLORS.AUTUMN }}>
              Add to My Store Catalog
            </button>
          </form>

          {/* NEW: Bulk Upload Modal/Section */}
          {showBulkUploadModal && (
            <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-2xl p-6 sm:p-8 max-w-lg w-full relative">
                <button
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    setSelectedBulkFile(null); // Clear file on close
                    setUploadProgress(0); // Reset progress on close
                  }}
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  &times;
                </button>
                <h2 className="text-2xl font-bold text-[#d45614] mb-5 text-center">Bulk Add Designs to Your Store</h2>
                <p className="mb-4 text-gray-700">
                  Upload a CSV file containing multiple designs to quickly populate your store catalog. Main product images need to be added manually after bulk upload. Customization images can be provided as public URLs in the CSV.
                </p>
                <p className="mb-4 text-gray-700 font-semibold">
                  Note: For bulk uploads, "Are customizations possible?" will default to "No" unless explicitly set to "Yes" in the CSV.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="w-full mb-6 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
                >
                  Download CSV Template
                </button>

                <div className="mb-4">
                  <label htmlFor="bulkCsvFile" className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File:
                  </label>
                  <input
                    type="file"
                    id="bulkCsvFile"
                    accept=".csv"
                    onChange={handleBulkFileChange}
                    className="w-full text-gray-700 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-2"
                  />
                  {selectedBulkFile && (
                    <p className="mt-2 text-sm text-gray-600">Selected file: {selectedBulkFile.name}</p>
                  )}
                </div>

                <button
                  onClick={handleBulkUpload}
                  disabled={!selectedBulkFile}
                  className="w-full mt-4   text-white font-semibold py-3 px-6 rounded-lg transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: COLORS.AUTUMN }}
                >
                  Upload CSV
                </button>
                <button
                  onClick={() => {
                      setShowBulkUploadModal(false);
                      setSelectedBulkFile(null); // Clear file on close
                      setUploadProgress(0); // Reset progress on cancel
                  }}
                  className="w-full mt-3 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-6 rounded-lg transition duration-150 ease-in-out"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      } />
    </div>
  );
};

export default UploadProduct;